import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { curriculumLibraryAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Home, Plus, Trash2, Loader2, BookOpen, ChevronRight, ChevronDown, Upload,
  Library as LibIcon, RefreshCw, ArrowUp, ArrowDown, CheckSquare, Square,
  FileText, Edit3, Save, X, Sparkles, PackageOpen, ListPlus,
} from 'lucide-react';

const BOARDS = ['CBSE', 'ICSE', 'IB', 'Cambridge', 'State Board', 'Other'];
const SESSIONS = ['2025-26', '2026-27', '2027-28', '2028-29', '2029-30'];
const CLASSES = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const RESOURCE_TYPES = [
  { key: 'book', label: 'Textbook' },
  { key: 'syllabus', label: 'Syllabus / Curriculum Guide' },
  { key: 'sample_paper', label: 'Sample / Past Paper' },
  { key: 'reference', label: 'Reference' },
];

const AdminCurriculumLibrary = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [filters, setFilters] = useState({
    board: 'CBSE', session: '2025-26', class_name: '', subject: '', resource_type: '', q: '',
  });
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // { book_id: bool }
  const [chapters, setChapters] = useState({}); // { book_id: [] }
  const [topics, setTopics] = useState({}); // { chapter_id: [] }
  const [showCreateBook, setShowCreateBook] = useState(false);
  const [newBook, setNewBook] = useState({
    board: 'CBSE', session: '2025-26', class_name: '10', subject: 'Mathematics',
    resource_type: 'book', book_name: '', publisher: '', edition: '', notes: '',
  });
  const [creatingBook, setCreatingBook] = useState(false);
  const [migrating, setMigrating] = useState(false);

  // Bulk-folder upload state
  const [showFolderUpload, setShowFolderUpload] = useState(false);
  const [folderMeta, setFolderMeta] = useState({ board: 'CBSE', session: '2025-26', class_name: '10', subject: '', resource_type: 'book', book_name: '' });
  const [folderFiles, setFolderFiles] = useState([]);
  const [folderUploading, setFolderUploading] = useState(false);
  const [folderProgress, setFolderProgress] = useState(0);
  const [folderResult, setFolderResult] = useState(null);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const folderInputRef = useRef(null);

  // Bulk paste-chapters state
  const [showBulkPaste, setShowBulkPaste] = useState(false);
  const [bulkPasteBook, setBulkPasteBook] = useState(null);
  const [bulkPasteText, setBulkPasteText] = useState('');
  const [bulkPasteMode, setBulkPasteMode] = useState('append');
  const [bulkPasting, setBulkPasting] = useState(false);
  const [bulkPasteResult, setBulkPasteResult] = useState(null);

  // Extraction modal state
  const [extractOpen, setExtractOpen] = useState(false);
  const [extractBook, setExtractBook] = useState(null);
  const [extractFile, setExtractFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [extractedChapters, setExtractedChapters] = useState([]);
  const [approving, setApproving] = useState(false);
  const extractFileRef = useRef(null);

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await curriculumLibraryAPI.listBooks(params);
      setBooks(data || []);
    } catch { toast.error('Failed to load books'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (isAdmin) loadBooks(); }, [isAdmin, loadBooks]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center p-6">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-2">Admins only</h2>
          <p className="text-stone-600 mb-4">Only administrators can manage the curriculum library.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-orange-500 text-white">Back</Button>
        </Card>
      </div>
    );
  }

  const toggleExpand = async (book) => {
    const bid = book.book_id;
    const wasOpen = !!expanded[bid];
    setExpanded({ ...expanded, [bid]: !wasOpen });
    if (!wasOpen && !chapters[bid]) {
      try {
        const { data } = await curriculumLibraryAPI.listChapters(bid);
        setChapters((prev) => ({ ...prev, [bid]: data || [] }));
      } catch { toast.error('Failed to load chapters'); }
    }
  };

  const loadTopics = async (chapterId) => {
    try {
      const { data } = await curriculumLibraryAPI.listTopics(chapterId);
      setTopics((prev) => ({ ...prev, [chapterId]: data || [] }));
    } catch { toast.error('Failed to load topics'); }
  };

  const handleCreateBook = async () => {
    if (!newBook.book_name.trim()) { toast.error('Book name is required'); return; }
    setCreatingBook(true);
    try {
      await curriculumLibraryAPI.createBook(newBook);
      toast.success('Book created');
      setShowCreateBook(false);
      setNewBook({ ...newBook, book_name: '', publisher: '', edition: '', notes: '' });
      await loadBooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setCreatingBook(false); }
  };

  const handleDeleteBook = async (book) => {
    if (!window.confirm(`Delete "${book.book_name}" and ALL its chapters/topics/files?`)) return;
    try {
      await curriculumLibraryAPI.deleteBook(book.book_id);
      toast.success('Book deleted');
      await loadBooks();
    } catch { toast.error('Delete failed'); }
  };

  const openFolderUpload = () => {
    setFolderFiles([]);
    setFolderResult(null);
    setDuplicateInfo(null);
    setFolderProgress(0);
    setFolderMeta({
      board: filters.board || 'CBSE',
      session: filters.session || '2025-26',
      class_name: filters.class_name || '10',
      subject: filters.subject || '',
      resource_type: 'book',
      book_name: '',
    });
    setShowFolderUpload(true);
  };

  const handleFolderPick = (e) => {
    const list = Array.from(e.target.files || []);
    setFolderFiles(list);
    // Auto-infer book name from top-level folder if any
    if (list.length) {
      const rp = list[0].webkitRelativePath || '';
      const inferred = rp.includes('/') ? rp.split('/', 1)[0] : '';
      if (inferred && !folderMeta.book_name) {
        setFolderMeta((m) => ({ ...m, book_name: inferred }));
      }
    }
  };

  const submitFolderUpload = async (mode = 'ask') => {
    if (!folderFiles.length) { toast.error('Choose a book folder (or files) first'); return; }
    if (!folderMeta.board || !folderMeta.session || !folderMeta.class_name || !folderMeta.subject) {
      toast.error('Fill Board, Session, Class and Subject'); return;
    }
    setFolderUploading(true);
    setFolderProgress(0);
    try {
      const { data } = await curriculumLibraryAPI.uploadFolder(folderFiles, {
        ...folderMeta, duplicate_mode: mode,
      }, (e) => { if (e.total) setFolderProgress(Math.round((e.loaded / e.total) * 100)); });
      setFolderResult(data);
      setDuplicateInfo(null);
      toast.success(`${data.book_name} — ${data.chapters_created} chapter(s) created`);
      await loadBooks();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && detail.code === 'DUPLICATE_BOOK') {
        setDuplicateInfo(detail);
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Folder upload failed');
      }
    } finally { setFolderUploading(false); }
  };

  const openBulkPaste = (book) => {
    setBulkPasteBook(book);
    setBulkPasteText('');
    setBulkPasteMode('append');
    setBulkPasteResult(null);
    setShowBulkPaste(true);
  };

  const submitBulkPaste = async () => {
    if (!bulkPasteText.trim()) { toast.error('Paste at least one chapter title'); return; }
    setBulkPasting(true);
    try {
      const { data } = await curriculumLibraryAPI.bulkChapters(bulkPasteBook.book_id, {
        text: bulkPasteText, mode: bulkPasteMode,
      });
      setBulkPasteResult(data);
      toast.success(`Created ${data.chapters_created} chapter(s)${data.skipped_duplicates?.length ? ` · ${data.skipped_duplicates.length} duplicate(s) skipped` : ''}`);
      // Refresh chapters + book counts
      setChapters((prev) => { const next = { ...prev }; delete next[bulkPasteBook.book_id]; return next; });
      const { data: ch } = await curriculumLibraryAPI.listChapters(bulkPasteBook.book_id);
      setChapters((prev) => ({ ...prev, [bulkPasteBook.book_id]: ch || [] }));
      setExpanded((prev) => ({ ...prev, [bulkPasteBook.book_id]: true }));
      await loadBooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk create failed');
    } finally { setBulkPasting(false); }
  };

  const handleMigrate = async () => {
    if (!window.confirm('Migrate legacy Curriculum Documents into the new Library? Safe to run multiple times.')) return;
    setMigrating(true);
    try {
      const { data } = await curriculumLibraryAPI.migrate();
      toast.success(`Migrated: ${data.books_created} books · ${data.chapters_created} chapters (scanned ${data.legacy_scanned})`);
      await loadBooks();
    } catch { toast.error('Migration failed'); }
    finally { setMigrating(false); }
  };

  const openExtract = (book) => {
    setExtractBook(book);
    setExtractedChapters([]);
    setExtractFile(null);
    setExtractProgress(0);
    setExtractOpen(true);
  };

  const handleExtract = async () => {
    if (!extractFile) { toast.error('Choose a PDF or DOCX'); return; }
    setExtracting(true);
    setExtractProgress(0);
    try {
      const { data } = await curriculumLibraryAPI.uploadExtract(extractBook.book_id, extractFile, (e) => {
        if (e.total) setExtractProgress(Math.round((e.loaded / e.total) * 100));
      });
      setExtractedChapters(data.extracted_chapters || []);
      if (!(data.extracted_chapters || []).length) {
        toast.warning('No chapters detected. Try a different file or add manually below.');
      } else {
        toast.success(`Detected ${data.extracted_chapters.length} chapter(s) — review before saving`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally { setExtracting(false); }
  };

  const toggleExtractedChapter = (i) => {
    const arr = [...extractedChapters];
    arr[i].selected = !arr[i].selected;
    setExtractedChapters(arr);
  };

  const toggleExtractedTopic = (ci, ti) => {
    const arr = [...extractedChapters];
    arr[ci].topics[ti].selected = !arr[ci].topics[ti].selected;
    setExtractedChapters(arr);
  };

  const editExtractedChapterTitle = (i, val) => {
    const arr = [...extractedChapters];
    arr[i].title = val;
    setExtractedChapters(arr);
  };

  const editExtractedTopicTitle = (ci, ti, val) => {
    const arr = [...extractedChapters];
    arr[ci].topics[ti].title = val;
    setExtractedChapters(arr);
  };

  const handleApprove = async () => {
    const selectedCount = extractedChapters.filter((c) => c.selected).length;
    if (selectedCount === 0) { toast.error('Select at least one chapter to save'); return; }
    setApproving(true);
    try {
      const payload = extractedChapters.map((c) => ({
        title: c.title, number: c.number || '', description: c.description || '',
        selected: !!c.selected,
        topics: (c.topics || []).map((t) => ({ title: t.title, number: t.number || '', description: t.description || '' })),
      }));
      const { data } = await curriculumLibraryAPI.approveExtraction(extractBook.book_id, payload);
      toast.success(`Saved ${data.chapters_created} chapters · ${data.topics_created} topics`);
      setExtractOpen(false);
      setExtractedChapters([]);
      // Force reload of chapters for this book
      setChapters((prev) => { const next = { ...prev }; delete next[extractBook.book_id]; return next; });
      setExpanded((prev) => ({ ...prev, [extractBook.book_id]: true }));
      const { data: ch } = await curriculumLibraryAPI.listChapters(extractBook.book_id);
      setChapters((prev) => ({ ...prev, [extractBook.book_id]: ch || [] }));
      await loadBooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setApproving(false); }
  };

  const handleAddChapter = async (bookId) => {
    const title = window.prompt('New chapter title:');
    if (!title || !title.trim()) return;
    try {
      await curriculumLibraryAPI.createChapter({ book_id: bookId, title: title.trim(), number: '', description: '', order_index: 0 });
      const { data } = await curriculumLibraryAPI.listChapters(bookId);
      setChapters((prev) => ({ ...prev, [bookId]: data || [] }));
      toast.success('Chapter added');
    } catch { toast.error('Failed to add chapter'); }
  };

  const handleDeleteChapter = async (bookId, chapter) => {
    if (!window.confirm(`Delete chapter "${chapter.title}"?`)) return;
    try {
      await curriculumLibraryAPI.deleteChapter(chapter.chapter_id);
      const { data } = await curriculumLibraryAPI.listChapters(bookId);
      setChapters((prev) => ({ ...prev, [bookId]: data || [] }));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleRenameChapter = async (bookId, chapter) => {
    const title = window.prompt('New title:', chapter.title);
    if (!title || !title.trim() || title === chapter.title) return;
    try {
      await curriculumLibraryAPI.updateChapter(chapter.chapter_id, { title: title.trim() });
      const { data } = await curriculumLibraryAPI.listChapters(bookId);
      setChapters((prev) => ({ ...prev, [bookId]: data || [] }));
      toast.success('Renamed');
    } catch { toast.error('Rename failed'); }
  };

  const moveChapter = async (bookId, index, direction) => {
    const chs = [...(chapters[bookId] || [])];
    const swap = index + direction;
    if (swap < 0 || swap >= chs.length) return;
    [chs[index], chs[swap]] = [chs[swap], chs[index]];
    try {
      await curriculumLibraryAPI.reorderChapters(bookId, chs.map((c) => c.chapter_id));
      setChapters((prev) => ({ ...prev, [bookId]: chs }));
    } catch { toast.error('Reorder failed'); }
  };

  const handleAddTopic = async (chapterId) => {
    const title = window.prompt('New topic title:');
    if (!title || !title.trim()) return;
    try {
      await curriculumLibraryAPI.createTopic({ chapter_id: chapterId, title: title.trim(), number: '', description: '', learning_outcome: '', order_index: 0 });
      await loadTopics(chapterId);
      toast.success('Topic added');
    } catch { toast.error('Failed to add'); }
  };

  const handleDeleteTopic = async (chapterId, topicId) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await curriculumLibraryAPI.deleteTopic(topicId);
      await loadTopics(chapterId);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleRenameTopic = async (chapterId, topic) => {
    const title = window.prompt('New title:', topic.title);
    if (!title || !title.trim() || title === topic.title) return;
    try {
      await curriculumLibraryAPI.updateTopic(topic.topic_id, { title: title.trim() });
      await loadTopics(chapterId);
      toast.success('Renamed');
    } catch { toast.error('Rename failed'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50/30">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-orange-200/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="lib-home-btn" variant="ghost" onClick={() => navigate('/dashboard')} className="text-orange-700"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2">
            <LibIcon className="w-6 h-6 text-orange-600" />
            <h1 className="text-xl font-bold">Curriculum Library (Admin)</h1>
          </div>
          <div className="flex gap-2">
            <Button data-testid="lib-legacy-btn" variant="outline" onClick={() => navigate('/admin/curriculum')} className="border-orange-200 text-orange-700">
              <PackageOpen className="w-4 h-4 mr-1.5" /> Legacy
            </Button>
            <Button data-testid="lib-migrate-btn" onClick={handleMigrate} disabled={migrating} variant="outline" className="border-orange-200 text-orange-700">
              {migrating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />} Migrate Legacy
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Filters + Create */}
        <Card className="p-5 border border-orange-200/60 bg-white shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[120px]">
              <Label className="text-xs">Board</Label>
              <Select value={filters.board || 'any'} onValueChange={(v) => setFilters({ ...filters, board: v === 'any' ? '' : v })}>
                <SelectTrigger data-testid="lib-filter-board" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem>{BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs">Session</Label>
              <Select value={filters.session || 'any'} onValueChange={(v) => setFilters({ ...filters, session: v === 'any' ? '' : v })}>
                <SelectTrigger data-testid="lib-filter-session" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem>{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs">Class</Label>
              <Select value={filters.class_name || 'any'} onValueChange={(v) => setFilters({ ...filters, class_name: v === 'any' ? '' : v })}>
                <SelectTrigger data-testid="lib-filter-class" className="mt-1 h-9"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px] flex-1">
              <Label className="text-xs">Subject</Label>
              <Input data-testid="lib-filter-subject" value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} placeholder="e.g. Mathematics" className="mt-1 h-9" />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-xs">Resource Type</Label>
              <Select value={filters.resource_type || 'any'} onValueChange={(v) => setFilters({ ...filters, resource_type: v === 'any' ? '' : v })}>
                <SelectTrigger data-testid="lib-filter-restype" className="mt-1 h-9"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem>{RESOURCE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-[200px] flex-1">
              <Label className="text-xs">Search book name</Label>
              <Input data-testid="lib-filter-q" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search..." className="mt-1 h-9" />
            </div>
            <Button data-testid="lib-add-book-btn" onClick={() => setShowCreateBook(true)} className="h-9 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Add Book
            </Button>
            <Button data-testid="lib-upload-folder-btn" onClick={openFolderUpload} className="h-9 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
              <PackageOpen className="w-4 h-4 mr-1.5" /> Upload Book Folder
            </Button>
          </div>
        </Card>

        {/* Books tree */}
        <Card className="p-5 border border-orange-200/60 bg-white shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-bold text-orange-800">Books ({books.length})</h2>
            <Button data-testid="lib-refresh-btn" variant="ghost" size="sm" onClick={loadBooks}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-orange-500" /></div>
          ) : books.length === 0 ? (
            <div className="py-10 text-center text-stone-500">
              <BookOpen className="w-10 h-10 mx-auto mb-2 text-stone-300" />
              <p>No books yet. Click <strong>Add Book</strong> to start, or <strong>Migrate Legacy</strong> if you have older uploads.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="lib-book-list">
              {books.map((b) => (
                <div key={b.book_id} className="border border-stone-200 rounded-lg" data-testid={`lib-book-${b.book_id}`}>
                  <div className="flex items-center gap-2 p-3 bg-orange-50/40 hover:bg-orange-50/70 cursor-pointer" onClick={() => toggleExpand(b)}>
                    {expanded[b.book_id] ? <ChevronDown className="w-4 h-4 text-orange-600" /> : <ChevronRight className="w-4 h-4 text-orange-600" />}
                    <BookOpen className="w-4 h-4 text-orange-600" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{b.book_name}</div>
                      <div className="text-xs text-stone-500">
                        {b.board} · {b.session} · Class {b.class_name} · {b.subject} · {b.resource_type} · {b.chapter_count || 0} chapters · {b.file_count || 0} files
                      </div>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button data-testid={`lib-extract-btn-${b.book_id}`} size="sm" variant="outline" onClick={() => openExtract(b)} className="h-7 text-xs border-orange-200 text-orange-700">
                        <Sparkles className="w-3 h-3 mr-1" /> Smart Upload
                      </Button>
                      <Button data-testid={`lib-paste-btn-${b.book_id}`} size="sm" variant="outline" onClick={() => openBulkPaste(b)} className="h-7 text-xs border-purple-200 text-purple-700">
                        <ListPlus className="w-3 h-3 mr-1" /> Paste Chapters
                      </Button>
                      <Button data-testid={`lib-add-chapter-btn-${b.book_id}`} size="sm" variant="outline" onClick={() => handleAddChapter(b.book_id)} className="h-7 text-xs">
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button data-testid={`lib-delete-book-${b.book_id}`} size="sm" variant="ghost" onClick={() => handleDeleteBook(b)} className="h-7 text-xs text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {expanded[b.book_id] && (
                    <div className="border-t border-stone-200 p-3 bg-white">
                      {(chapters[b.book_id] || []).length === 0 ? (
                        <p className="text-xs text-stone-400 py-3 text-center">No chapters yet. Use <strong>Smart Upload</strong> to auto-detect, or <Plus className="inline w-3 h-3" /> to add manually.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(chapters[b.book_id] || []).map((c, ci) => (
                            <ChapterRow
                              key={c.chapter_id}
                              chapter={c}
                              index={ci}
                              total={chapters[b.book_id].length}
                              topics={topics[c.chapter_id]}
                              onExpand={() => loadTopics(c.chapter_id)}
                              onRename={() => handleRenameChapter(b.book_id, c)}
                              onDelete={() => handleDeleteChapter(b.book_id, c)}
                              onMove={(dir) => moveChapter(b.book_id, ci, dir)}
                              onAddTopic={() => handleAddTopic(c.chapter_id)}
                              onRenameTopic={(t) => handleRenameTopic(c.chapter_id, t)}
                              onDeleteTopic={(tid) => handleDeleteTopic(c.chapter_id, tid)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>

      {/* Create book modal */}
      <Dialog open={showCreateBook} onOpenChange={setShowCreateBook}>
        <DialogContent data-testid="lib-create-book-modal" className="max-w-lg">
          <DialogHeader><DialogTitle>Add a new Book / Resource</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div>
              <Label className="text-xs">Board</Label>
              <Select value={newBook.board} onValueChange={(v) => setNewBook({ ...newBook, board: v })}>
                <SelectTrigger data-testid="lib-newbook-board" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Session</Label>
              <Select value={newBook.session} onValueChange={(v) => setNewBook({ ...newBook, session: v })}>
                <SelectTrigger data-testid="lib-newbook-session" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Class</Label>
              <Select value={newBook.class_name} onValueChange={(v) => setNewBook({ ...newBook, class_name: v })}>
                <SelectTrigger data-testid="lib-newbook-class" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input data-testid="lib-newbook-subject" value={newBook.subject} onChange={(e) => setNewBook({ ...newBook, subject: e.target.value })} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-xs">Resource Type</Label>
              <Select value={newBook.resource_type} onValueChange={(v) => setNewBook({ ...newBook, resource_type: v })}>
                <SelectTrigger data-testid="lib-newbook-restype" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{RESOURCE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Publisher</Label>
              <Input data-testid="lib-newbook-publisher" value={newBook.publisher} onChange={(e) => setNewBook({ ...newBook, publisher: e.target.value })} placeholder="NCERT / Oxford" className="mt-1 h-9" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Book / Resource Name *</Label>
              <Input data-testid="lib-newbook-name" value={newBook.book_name} onChange={(e) => setNewBook({ ...newBook, book_name: e.target.value })} placeholder="NCERT Mathematics — Class 10" className="mt-1 h-9" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea data-testid="lib-newbook-notes" value={newBook.notes} onChange={(e) => setNewBook({ ...newBook, notes: e.target.value })} rows={2} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateBook(false)}>Cancel</Button>
            <Button data-testid="lib-newbook-save-btn" onClick={handleCreateBook} disabled={creatingBook} className="bg-orange-500 text-white">
              {creatingBook ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Paste Chapters modal */}
      <Dialog open={showBulkPaste} onOpenChange={setShowBulkPaste}>
        <DialogContent data-testid="lib-bulk-paste-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListPlus className="w-5 h-5 text-purple-600" /> Paste Chapters — {bulkPasteBook?.book_name}
            </DialogTitle>
            <DialogDescription>Paste a chapter list (plain lines, numbered, CSV or TSV) and we'll create them in one shot.</DialogDescription>
          </DialogHeader>
          {bulkPasteResult ? (
            <div className="py-3 space-y-2" data-testid="lib-bulk-paste-result">
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">
                Created <strong>{bulkPasteResult.chapters_created}</strong> chapter(s).
                {bulkPasteResult.skipped_duplicates?.length ? ` · Skipped ${bulkPasteResult.skipped_duplicates.length} duplicate title(s).` : ''}
              </p>
              {bulkPasteResult.skipped_duplicates?.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 max-h-32 overflow-y-auto">
                  <strong>Skipped:</strong> {bulkPasteResult.skipped_duplicates.join(' · ')}
                </div>
              )}
              <div className="flex justify-end">
                <Button data-testid="lib-bulk-paste-close" onClick={() => setShowBulkPaste(false)} className="bg-purple-600 text-white">Done</Button>
              </div>
            </div>
          ) : (
            <div className="py-3 space-y-3">
              <p className="text-sm text-stone-600">
                Paste your chapter list — one per line, or with numbers, or as CSV/TSV. Supported formats:
              </p>
              <pre className="text-[11px] bg-stone-50 border border-stone-200 rounded p-2 whitespace-pre-wrap">{`Chapter One
Chapter Two

1. Real Numbers
2) Polynomials
Chapter 3 - Triangles

# CSV / TSV also works:
1, Real Numbers, Introduction to real numbers
2, Polynomials, Zeroes and coefficients`}</pre>
              <div>
                <Label className="text-xs">Chapter list</Label>
                <Textarea
                  data-testid="lib-bulk-paste-text"
                  value={bulkPasteText}
                  onChange={(e) => setBulkPasteText(e.target.value)}
                  rows={10}
                  className="mt-1 font-mono text-sm"
                  placeholder="Paste chapters here…"
                />
                <p className="text-[11px] text-stone-500 mt-1">
                  Detected lines: <strong data-testid="lib-bulk-paste-count">{bulkPasteText.split('\n').filter((l) => l.trim() && !l.trim().startsWith('#')).length}</strong>
                </p>
              </div>
              <div>
                <Label className="text-xs">Mode</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    data-testid="lib-bulk-paste-mode-append"
                    variant={bulkPasteMode === 'append' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBulkPasteMode('append')}
                    className={bulkPasteMode === 'append' ? 'bg-purple-600 text-white' : ''}
                  >
                    Append (skip duplicates)
                  </Button>
                  <Button
                    data-testid="lib-bulk-paste-mode-replace"
                    variant={bulkPasteMode === 'replace' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBulkPasteMode('replace')}
                    className={bulkPasteMode === 'replace' ? 'bg-red-600 text-white' : ''}
                  >
                    Replace (delete existing first)
                  </Button>
                </div>
                {bulkPasteMode === 'replace' && (
                  <p className="text-[11px] text-red-700 mt-1">⚠ Existing chapters, their topics, and attached files will be deleted first.</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowBulkPaste(false)}>Cancel</Button>
                <Button
                  data-testid="lib-bulk-paste-submit"
                  onClick={submitBulkPaste}
                  disabled={bulkPasting || !bulkPasteText.trim()}
                  className="bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white"
                >
                  {bulkPasting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ListPlus className="w-4 h-4 mr-1" />}
                  Create Chapters
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Folder Upload modal */}
      <Dialog open={showFolderUpload} onOpenChange={setShowFolderUpload}>
        <DialogContent data-testid="lib-folder-upload-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageOpen className="w-5 h-5 text-emerald-600" /> Upload Book Folder
            </DialogTitle>
            <DialogDescription>Pick a whole book folder — folder name becomes the Book, each file becomes a Chapter, natural order preserved.</DialogDescription>
          </DialogHeader>
          {folderResult ? (
            <div className="py-3 space-y-2" data-testid="lib-folder-result">
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">
                <strong>{folderResult.book_name}</strong> — {folderResult.chapters_created} chapter(s) saved.
                {folderResult.skipped?.length ? ` · ${folderResult.skipped.length} skipped.` : ''}
                {folderResult.unsupported?.length ? ` · ${folderResult.unsupported.length} unsupported file(s).` : ''}
              </p>
              <div className="max-h-60 overflow-y-auto text-xs border border-stone-200 rounded p-2">
                {folderResult.chapters.map((c, i) => (
                  <div key={c.chapter_id} className="flex items-center gap-2 py-1" data-testid={`lib-folder-result-ch-${i}`}>
                    <CheckSquare className="w-3.5 h-3.5 text-emerald-600" />
                    <span className="text-stone-500 w-6 text-right">{c.number ?? i + 1}.</span>
                    <span className="flex-1 truncate">{c.title}</span>
                    <span className="text-[10px] text-stone-400">{(c.text_length / 1024).toFixed(1)} KB text</span>
                  </div>
                ))}
                {(folderResult.skipped || []).map((s, i) => (
                  <div key={`sk-${i}`} className="flex items-center gap-2 py-1 text-amber-700">
                    <Square className="w-3.5 h-3.5" /> <span>{s.filename}</span> <span className="text-[10px]">({s.reason})</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button data-testid="lib-folder-close-btn" onClick={() => setShowFolderUpload(false)} className="bg-emerald-600 text-white">Done</Button>
              </div>
            </div>
          ) : duplicateInfo ? (
            <div className="py-3 space-y-3" data-testid="lib-folder-duplicate-dialog">
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                {duplicateInfo.message} It currently has <strong>{duplicateInfo.existing_chapter_count}</strong> chapter(s).
              </p>
              <p className="text-xs text-stone-600">What would you like to do?</p>
              <div className="flex flex-col gap-2">
                <Button data-testid="lib-folder-duplicate-update" onClick={() => submitFolderUpload('update')} disabled={folderUploading} className="bg-orange-500 text-white">Update Existing Book (skip duplicate chapter titles)</Button>
                <Button data-testid="lib-folder-duplicate-newversion" onClick={() => submitFolderUpload('new_version')} disabled={folderUploading} variant="outline">Create as New Version (v2, v3, …)</Button>
                <Button data-testid="lib-folder-duplicate-cancel" onClick={() => setDuplicateInfo(null)} variant="ghost">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="py-3 space-y-3">
              <p className="text-sm text-stone-600">
                Pick a whole book folder — the folder name becomes the <strong>Book Name</strong> and each file name becomes a <strong>Chapter</strong>. Order is auto-detected. Supported: PDF, DOCX, TXT (up to 500 MB each).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Board *</Label>
                  <Select value={folderMeta.board} onValueChange={(v) => setFolderMeta({ ...folderMeta, board: v })}>
                    <SelectTrigger data-testid="lib-folder-board" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{BOARDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Session *</Label>
                  <Select value={folderMeta.session} onValueChange={(v) => setFolderMeta({ ...folderMeta, session: v })}>
                    <SelectTrigger data-testid="lib-folder-session" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{SESSIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Class *</Label>
                  <Select value={folderMeta.class_name} onValueChange={(v) => setFolderMeta({ ...folderMeta, class_name: v })}>
                    <SelectTrigger data-testid="lib-folder-class" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Subject *</Label>
                  <Input data-testid="lib-folder-subject" value={folderMeta.subject} onChange={(e) => setFolderMeta({ ...folderMeta, subject: e.target.value })} placeholder="Sanskrit / Mathematics" className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">Resource Type</Label>
                  <Select value={folderMeta.resource_type} onValueChange={(v) => setFolderMeta({ ...folderMeta, resource_type: v })}>
                    <SelectTrigger data-testid="lib-folder-restype" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{RESOURCE_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Book Name (auto-detected from folder)</Label>
                  <Input data-testid="lib-folder-bookname" value={folderMeta.book_name} onChange={(e) => setFolderMeta({ ...folderMeta, book_name: e.target.value })} placeholder="Auto" className="mt-1 h-9" />
                </div>
              </div>
              <div className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center bg-emerald-50/40">
                <input ref={folderInputRef} data-testid="lib-folder-input" type="file" webkitdirectory="" directory="" multiple onChange={handleFolderPick} className="hidden" accept=".pdf,.docx,.txt" />
                <input data-testid="lib-folder-files-input" type="file" multiple onChange={handleFolderPick} className="hidden" accept=".pdf,.docx,.txt" id="lib-folder-files-input" />
                <PackageOpen className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
                <p className="text-sm text-stone-700 mb-3">Pick the whole book folder (or select files manually)</p>
                <div className="flex justify-center gap-2 flex-wrap">
                  <Button data-testid="lib-folder-choose-folder-btn" variant="outline" onClick={() => folderInputRef.current?.click()} className="border-emerald-400 text-emerald-700">
                    <PackageOpen className="w-4 h-4 mr-1.5" /> Choose Folder
                  </Button>
                  <Button data-testid="lib-folder-choose-files-btn" variant="ghost" onClick={() => document.getElementById('lib-folder-files-input').click()} className="text-stone-600">
                    or choose files
                  </Button>
                </div>
                {folderFiles.length > 0 && (
                  <p className="text-xs text-emerald-800 mt-3" data-testid="lib-folder-files-count">
                    {folderFiles.length} file(s) selected · Book name: <strong>{folderMeta.book_name || '(auto)'}</strong>
                  </p>
                )}
              </div>
              {folderUploading && (
                <div>
                  <div className="w-full bg-stone-200 rounded-full h-2 overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" style={{ width: `${folderProgress}%` }} />
                  </div>
                  <p className="text-xs text-emerald-700 mt-1">Uploading + processing chapters — {folderProgress}%</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowFolderUpload(false)}>Cancel</Button>
                <Button data-testid="lib-folder-submit-btn" onClick={() => submitFolderUpload('ask')} disabled={folderUploading || !folderFiles.length} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                  {folderUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload & Detect Chapters
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Smart Upload + Approve modal */}
      <Dialog open={extractOpen} onOpenChange={setExtractOpen}>
        <DialogContent data-testid="lib-extract-modal" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-600" /> Smart Upload — {extractBook?.book_name}
            </DialogTitle>
          </DialogHeader>
          {extractedChapters.length === 0 ? (
            <div className="py-3 space-y-3">
              <p className="text-sm text-stone-600">Upload the whole book / syllabus PDF (or DOCX). We'll auto-detect chapters + topics and let you tick the ones to save.</p>
              <input ref={extractFileRef} data-testid="lib-extract-file" type="file" accept=".pdf,.docx" onChange={(e) => setExtractFile(e.target.files?.[0])} className="hidden" />
              <Button variant="outline" onClick={() => extractFileRef.current?.click()} className="w-full border-dashed border-2 border-orange-300 py-6" data-testid="lib-extract-choose-btn">
                <Upload className="w-5 h-5 mr-2 text-orange-600" />
                {extractFile ? extractFile.name : 'Click to choose PDF / DOCX (up to 500 MB)'}
              </Button>
              {extracting && (
                <div>
                  <div className="w-full bg-stone-200 rounded-full h-2 overflow-hidden">
                    <div className="h-2 bg-gradient-to-r from-orange-500 to-amber-500 transition-all" style={{ width: `${extractProgress}%` }} />
                  </div>
                  <p className="text-xs text-orange-700 mt-1">Uploading + running AI extraction — {extractProgress}%</p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setExtractOpen(false)}>Cancel</Button>
                <Button data-testid="lib-extract-run-btn" onClick={handleExtract} disabled={!extractFile || extracting} className="bg-orange-500 text-white">
                  {extracting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Extract Chapters
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-3 space-y-3">
              <p className="text-sm text-stone-600">We detected <strong>{extractedChapters.length}</strong> chapters. Tick the ones to save, edit titles inline, then click <strong>Save Selected</strong>.</p>
              <div className="max-h-[55vh] overflow-y-auto border border-stone-200 rounded-lg p-3 space-y-2" data-testid="lib-extract-list">
                {extractedChapters.map((c, ci) => (
                  <div key={ci} className="border border-stone-200 rounded p-2" data-testid={`lib-extract-chapter-${ci}`}>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => toggleExtractedChapter(ci)} data-testid={`lib-extract-toggle-${ci}`}>
                        {c.selected ? <CheckSquare className="w-4 h-4 text-orange-600" /> : <Square className="w-4 h-4 text-stone-400" />}
                      </button>
                      {c.number && <span className="text-xs bg-orange-100 text-orange-800 px-1.5 rounded">{c.number}</span>}
                      <Input
                        value={c.title}
                        onChange={(e) => editExtractedChapterTitle(ci, e.target.value)}
                        className="h-7 text-sm flex-1 font-semibold"
                        data-testid={`lib-extract-title-${ci}`}
                      />
                    </div>
                    {c.topics && c.topics.length > 0 && (
                      <div className="ml-6 mt-2 space-y-1">
                        {c.topics.map((t, ti) => (
                          <div key={ti} className="flex items-center gap-2 text-xs">
                            <button type="button" onClick={() => toggleExtractedTopic(ci, ti)}>
                              {t.selected ? <CheckSquare className="w-3 h-3 text-emerald-600" /> : <Square className="w-3 h-3 text-stone-400" />}
                            </button>
                            {t.number && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1 rounded">{t.number}</span>}
                            <Input
                              value={t.title}
                              onChange={(e) => editExtractedTopicTitle(ci, ti, e.target.value)}
                              className="h-6 text-xs flex-1"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setExtractOpen(false)}>Cancel</Button>
                <Button data-testid="lib-extract-approve-btn" onClick={handleApprove} disabled={approving} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                  {approving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save Selected ({extractedChapters.filter((c) => c.selected).length})
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ChapterRow = ({ chapter, index, total, topics, onExpand, onRename, onDelete, onMove, onAddTopic, onRenameTopic, onDeleteTopic }) => {
  const [open, setOpen] = useState(false);
  const handleToggle = async () => {
    setOpen(!open);
    if (!open && !topics) await onExpand();
  };
  return (
    <div className="border border-stone-100 rounded" data-testid={`lib-chapter-${chapter.chapter_id}`}>
      <div className="flex items-center gap-2 p-2 hover:bg-stone-50 cursor-pointer" onClick={handleToggle}>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span className="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded w-8 text-center">{chapter.number || (index + 1)}</span>
        <span className="text-sm flex-1 truncate">{chapter.title}</span>
        <span className="text-[10px] text-stone-500">{chapter.topic_count || 0} topics</span>
        <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onMove(-1)} disabled={index === 0} data-testid={`lib-chapter-up-${chapter.chapter_id}`}><ArrowUp className="w-3 h-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => onMove(1)} disabled={index === total - 1} data-testid={`lib-chapter-down-${chapter.chapter_id}`}><ArrowDown className="w-3 h-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onRename} data-testid={`lib-chapter-rename-${chapter.chapter_id}`}><Edit3 className="w-3 h-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onAddTopic} data-testid={`lib-chapter-addtopic-${chapter.chapter_id}`}><Plus className="w-3 h-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" onClick={onDelete} data-testid={`lib-chapter-delete-${chapter.chapter_id}`}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      {open && (
        <div className="pl-8 pr-2 pb-2 space-y-0.5">
          {!topics ? <Loader2 className="w-3 h-3 animate-spin" /> :
            topics.length === 0 ? <p className="text-[11px] text-stone-400 italic py-1">No topics — click <Plus className="w-3 h-3 inline" /> to add</p> :
            topics.map((t) => (
              <div key={t.topic_id} className="flex items-center gap-1.5 py-0.5 text-xs" data-testid={`lib-topic-${t.topic_id}`}>
                {t.number && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1 rounded">{t.number}</span>}
                <span className="flex-1 truncate">{t.title}</span>
                <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => onRenameTopic(t)} data-testid={`lib-topic-rename-${t.topic_id}`}><Edit3 className="w-2.5 h-2.5" /></Button>
                <Button size="icon" variant="ghost" className="h-5 w-5 text-red-600" onClick={() => onDeleteTopic(t.topic_id)} data-testid={`lib-topic-delete-${t.topic_id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

export default AdminCurriculumLibrary;
